/**
 * Хук для работы с агентами
 * Интегрирует REST (начальная загрузка) + WS (обновления) данные
 */

import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { toast } from 'sonner';

import i18n from '../i18n/config';
import {
  agentQueryKeys,
  useCreateAgent,
  useDeleteAgent,
  useGetAgent,
  useListAgents,
  preflightAgentStart,
  useStartAgent,
  useStopAgent,
  type SimpleAgentCreateRequest
} from '../shared/api/hooks';
import { logError, WS_CHANNELS } from '../shared/config/constants';
import { QK } from '../shared/model/queryKeys';
import { humanizeDetailText } from '../shared/notifications';
import { useBusChannel, useBusEvents } from '../shared/ws/useBusSocket';

import type { EventKind } from '../shared/model/envelope';

// Переэкспорт типов для обратной совместимости
export type AgentCreateRequest = SimpleAgentCreateRequest;

/**
 * Основной хук для работы с агентами
 * Bootstrap из REST + realtime обновления из WS
 */
export const useAgents = () => {
  const queryClient = useQueryClient();
  const enableStartPreflight = String(import.meta.env.VITE_AGENT_START_PREFLIGHT ?? '0') === '1';

  // REST: начальная загрузка списка агентов
  const {
    data: agents = [],
    isLoading,
    error,
    refetch
  } = useListAgents();

  // WebSocket подписка на обновления статуса агентов
  // Подписываемся на события agent.status для всех агентов
  // Используем type assertion, так как 'agent.status' не входит в стандартный EventKind,
  // но поддерживается бэкендом через каналы
  useBusEvents(
    'agent.status' as EventKind,
    useCallback((event: unknown) => {
      try {
        const payload = (event as { payload?: unknown; data?: unknown })?.payload ?? (event as { data?: unknown })?.data ?? event;
        if (payload && typeof payload === 'object') {
          const agentUpdate = payload as { id?: string; isRunning?: boolean; [key: string]: unknown };
          if (agentUpdate.id) {
            // Инвалидируем кэш конкретного агента
            queryClient.invalidateQueries({ queryKey: agentQueryKeys.get(agentUpdate.id) });
            // Инвалидируем список агентов
            queryClient.invalidateQueries({ queryKey: agentQueryKeys.list() });
          }
        }
      } catch (error) {
        logError('Failed to process agent status update', error);
      }
    }, [queryClient]),
    { enabled: true }
  );

  useBusEvents(
    'trading.signal',
    useCallback((event: unknown) => {
      try {
        const payload = (event as { payload?: unknown; data?: unknown })?.payload ?? (event as { data?: unknown })?.data ?? event;
        if (!payload || typeof payload !== 'object') return;
        const agentId = String((payload as { agent_id?: unknown; agentId?: unknown }).agent_id ?? (payload as { agentId?: unknown }).agentId ?? '').trim();
        if (agentId) {
          queryClient.invalidateQueries({ queryKey: agentQueryKeys.get(agentId) });
          queryClient.invalidateQueries({ queryKey: QK.tradeRuntime(agentId) });
        }
        queryClient.invalidateQueries({ queryKey: agentQueryKeys.list() });
      } catch (error) {
        logError('Failed to process trading signal update', error);
      }
    }, [queryClient]),
    { enabled: true }
  );

  useBusEvents(
    'trading.execution',
    useCallback((event: unknown) => {
      try {
        const payload = (event as { payload?: unknown; data?: unknown })?.payload ?? (event as { data?: unknown })?.data ?? event;
        if (!payload || typeof payload !== 'object') return;
        const agentId = String((payload as { agent_id?: unknown; agentId?: unknown }).agent_id ?? (payload as { agentId?: unknown }).agentId ?? '').trim();
        if (agentId) {
          queryClient.invalidateQueries({ queryKey: agentQueryKeys.get(agentId) });
          queryClient.invalidateQueries({ queryKey: QK.tradeRuntime(agentId) });
        }
        queryClient.invalidateQueries({ queryKey: agentQueryKeys.list() });
      } catch (error) {
        logError('Failed to process trading execution update for agents', error);
      }
    }, [queryClient]),
    { enabled: true }
  );

  // Мутации
  const createAgentMutation = useCreateAgent();
  const startAgentMutation = useStartAgent();
  const stopAgentMutation = useStopAgent();
  const deleteAgentMutation = useDeleteAgent();

  const startAgentSafely = useCallback(
    async (agentId: string) => {
      if (!enableStartPreflight) {
        startAgentMutation.mutate(agentId);
        return;
      }
      try {
        const preflight = await preflightAgentStart(agentId);
        const canStart = preflight.context_check?.can_start;
        const runContractCanStart = preflight.run_contract_check?.can_start;
        if (canStart === false) {
          logError('Start preflight failed: missing required context fields', {
            agentId,
            missing: preflight.context_check?.missing_fields ?? [],
          });
          toast.warning(i18n.t('notifications:toasts.startCancelledMissingFields'));
          return;
        }
        if (runContractCanStart === false) {
          logError('Start preflight failed: strategy spec contract is incomplete', {
            agentId,
          });
          toast.warning(i18n.t('notifications:toasts.startCancelledStrategyNotReady'));
          return;
        }
      } catch (err) {
        logError('Start preflight failed, start action cancelled', err);
        const msg = err instanceof Error ? err.message : String(err);
        toast.warning(humanizeDetailText(msg, i18n.t('notifications:toasts.startUnavailable')));
        return;
      }
      startAgentMutation.mutate(agentId);
    },
    [enableStartPreflight, startAgentMutation],
  );

  const isPending =
    startAgentMutation.isPending ||
    stopAgentMutation.isPending ||
    deleteAgentMutation.isPending;

  return {
    agents,
    isLoading,
    error,
    isPending,
    createAgent: createAgentMutation.mutate,
    startAgent: startAgentSafely,
    stopAgent: stopAgentMutation.mutate,
    deleteAgent: deleteAgentMutation.mutate,
    refetch,
  };
};

/**
 * Хук для работы с одним агентом
 * Интегрирует REST + WS данные
 */
export const useAgent = (agentId: string) => {
  const queryClient = useQueryClient();

  // REST: получение данных конкретного агента по ID
  const {
    data: agent,
    isLoading,
    error,
    refetch
  } = useGetAgent(agentId);

  // WebSocket подписка на обновления статуса конкретного агента
  useBusChannel(
    WS_CHANNELS.AGENT_STATUS(agentId),
    useCallback((event: unknown) => {
      try {
        const payload = (event as { payload?: unknown; data?: unknown })?.payload ?? (event as { data?: unknown })?.data ?? event;
        if (payload && typeof payload === 'object') {
          const agentUpdate = payload as { isRunning?: boolean; runId?: string; stats?: unknown; [key: string]: unknown };
          // Обновляем кэш агента
          queryClient.setQueryData(agentQueryKeys.get(agentId), (oldAgent: typeof agent) => {
            if (!oldAgent) return oldAgent;
            return {
              ...oldAgent,
              ...agentUpdate,
            };
          });
          // Инвалидируем список агентов для синхронизации
          queryClient.invalidateQueries({ queryKey: agentQueryKeys.list() });
        }
      } catch (error) {
        logError('Failed to process agent status update', error);
      }
    }, [agentId, queryClient]),
    { enabled: !!agentId, snapshot: true }
  );

  // WebSocket подписка на обновления статистики агента
  useBusChannel(
    WS_CHANNELS.AGENT_STATS(agentId),
    useCallback((event: unknown) => {
      try {
        const payload = (event as { payload?: unknown; data?: unknown })?.payload ?? (event as { data?: unknown })?.data ?? event;
        if (payload && typeof payload === 'object') {
          const statsUpdate = payload as { stats?: unknown; [key: string]: unknown };
          // Обновляем кэш агента со статистикой
          queryClient.setQueryData(agentQueryKeys.get(agentId), (oldAgent: typeof agent) => {
            if (!oldAgent) return oldAgent;
            return {
              ...oldAgent,
              stats: statsUpdate.stats ?? oldAgent.stats,
            };
          });
        }
      } catch (error) {
        logError('Failed to process agent stats update', error);
      }
    }, [agentId, queryClient]),
    { enabled: !!agentId }
  );

  // Методы для работы с агентом
  const isRunning = agent?.isRunning || false;
  const canStart = !isRunning;
  const canStop = isRunning;

  return {
    agentId,
    agent,
    isLoading,
    error,
    isRunning,
    canStart,
    canStop,
    refetch,
  };
};